import { ref, unref } from 'vue';
import globals from '@/globals';
import { escapeRegExp } from 'lodash/fp';
import { BrowserMode } from '@/store/modules/entity_browser/config';
import { routerKey } from 'vue-router';
import LeftMenu from '@/components/layout/LeftMenu.vue';
import Tags from '@/views/Tags.vue';
import SavedSearchFilters from '@/views/doc_browser/SavedSearchFilters.vue';
import DocumentBrowser from '@/views/doc_browser/DocumentBrowser.vue';
import UserFilesPage from '@/components/roles/UserFilesPage.vue';

const defaultRoutes = [
  {
    path: '.*',
    target: '#imperia_main_menu',
    component: LeftMenu,
    props: {},
  },
  {
    path: '/structure/tags',
    target: '#tags_page',
    component: Tags,
    props: {},
  },
  {
    path: '/document/saved_filters',
    target: '#doc_browser_filters',
    component: SavedSearchFilters,
    props: {},
  },
  {
    path: '/document/browser',
    target: '#doc_browser_page',
    component: DocumentBrowser,
    props: {},
  },
  {
    path: '/document/media',
    target: '#mam_page',
    component: DocumentBrowser,
    props: {
      mode: BrowserMode.MAM,
    },
  },
  {
    path: '/roles/user_files',
    target: '#page',
    component: UserFilesPage,
    props: {},
  },
];

/**
 * @param {*} location
 * @returns an object representation of the search params of the specified location
 */
function parseQuery(location) {
  return Object.fromEntries(new URLSearchParams(location?.search));
}

/**
 * Custom Router used to mount Vue components on static imperia pages (currently used only in production).
 * Should be replaced by the standard `vue-router` package once the SPA migration is completed.
 * Mimics the vue-router API to simplify the switch.
 *
 * @returns a Static Router plug-in
 * See: https://github.com/vuejs/router/blob/main/packages/router/src/router.ts
 */
export function createStaticRouter() {
  // API Ref: https://router.vuejs.org/api/interfaces/RouteLocation.html
  const currentRoute = ref({
    query: parseQuery(document.location),
  });

  /**
   * Programmatically navigate to a new URL
   * https://router.vuejs.org/api/#push
   *
   */
  function push(routeLocation = {}) {
    const { I } = globals;
    const { path, query } = routeLocation;

    document.location.href = I.Util.uri(path, query);
  }

  /**
   * Stub to prevent warnings in VueDevTools
   * Add a navigation hook that is executed after every navigation. Returns a function that removes the registered hook.
   */
  function afterEach() {} // eslint-disable-line

  const router = {
    currentRoute,
    push,
    afterEach,

    // Install the router plug-in:
    install(app) {
      // Expose the router instance to components
      app.provide(routerKey, router);
      app.config.globalProperties.$router = router; // eslint-disable-line

      Object.defineProperty(app.config.globalProperties, '$route', {
        enumerable: true,
        get: () => unref(currentRoute),
      });
    },
  };

  return router;
}

/**
 * Extracts the controller path from
 * the specified uri and prefix
 * @returns the path to the current controller
 */
export function resolveController(uri, prefix = '') {
  try {
    const decodedUri = decodeURIComponent(uri);
    const escapedPrefix = escapeRegExp(prefix);
    const match = decodedUri.match(new RegExp(`^${escapedPrefix}([^?&]*)`));
    return match && match[1];
  } catch (e) {
    return null;
  }
}

/**
 *
 * @param {*} location URL object or string
 * @param {*} systemConfig Imperia config specifying the URL prefix used
 * @param {*} routes list of route recrods
 * @returns an array of route records that match the specified URL location
 * related:
 * - https://next.router.vuejs.org/guide/essentials/route-matching-syntax.html
 * - https://paths.esm.dev/
 */
export function getMatchedRoutes(location, systemConfig = {}, routes = defaultRoutes) {
  try {
    const url = new URL(location);
    const { pathname, search } = url;
    const controller = resolveController(`${pathname}${search}`, systemConfig.prefix);
    return routes.filter((route) => new RegExp(`^${route.path}$`).test(controller));
  } catch (e) {
    console.error(e);
    return [];
  }
}
